import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, unwrap } from '../../api/client';
import { errorMessage } from '../../api/errors';
import { useAuth } from '../../auth/AuthProvider';
import { Button, Field, Input, Modal, Select } from '../../design';
import { useToast } from '../../design/Toast';
import { lockerOptionLabel, useLockerOptions } from '../operations/common';

const MIN_REASON = 10;

/** Cererea de deschidere administrativă (spec §7): motiv ≥ 10 caractere, step-up, aprobare separată. */
export function RemoteOpenDialog({
  onClose,
  onCreated,
  lockerId: initialLocker = '',
  slotId: initialSlot = '',
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
  lockerId?: string;
  slotId?: string;
}) {
  const { withStepUp } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const lockers = useLockerOptions();
  const [lockerId, setLockerId] = useState(initialLocker);
  const [slotId, setSlotId] = useState(initialSlot);
  const [reason, setReason] = useState('');
  const [ticket, setTicket] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const detail = useQuery({
    queryKey: ['locker', lockerId],
    queryFn: () => unwrap(api.GET('/lockers/{id}', { params: { path: { id: lockerId } } })),
    enabled: !!lockerId,
  });
  const slots = (detail.data?.slots ?? [])
    .filter((x) => x.techState !== 'blocked')
    .sort((a, b) => a.label.localeCompare(b.label, 'ro', { numeric: true }));

  const create = useMutation({
    mutationFn: () =>
      withStepUp((headers) =>
        unwrap(
          api.POST('/remote-open-requests', {
            params: { header: headers },
            body: { lockerId, slotId, reason: reason.trim(), ticket: ticket.trim() || undefined },
          }),
        ),
      ),
    onSuccess: (r) => {
      toast(
        'Cererea a fost înregistrată; așteaptă aprobarea unui alt utilizator (15 minute).',
        'success',
      );
      void qc.invalidateQueries({ queryKey: ['remote-open'] });
      onCreated(r.id);
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });

  const submit = () => {
    const next: Record<string, string> = {};
    if (!lockerId) next.lockerId = 'Alege un locker';
    if (!slotId) next.slotId = 'Alege un compartiment';
    if (reason.trim().length < MIN_REASON)
      next.reason = `Descrie intervenția în minimum ${MIN_REASON} caractere`;
    setErrors(next);
    if (Object.keys(next).length === 0) create.mutate();
  };

  return (
    <Modal
      title="Deschidere administrativă"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Renunță</Button>
          <Button variant="primary" icon="shield" onClick={submit} busy={create.isPending}>
            Continuă cu 2FA
          </Button>
        </>
      }
    >
      <div className="stack">
        <p className="small muted">
          Nu se folosește pentru livrări obișnuite. Cererea merge la un al doilea utilizator cu
          drept de aprobare; dispozitivul, expirarea și perimetrul se verifică din nou la execuție.
        </p>
        <Field label="Locker" error={errors.lockerId} htmlFor="ro-locker">
          <Select
            id="ro-locker"
            value={lockerId}
            onChange={(e) => {
              setLockerId(e.target.value);
              setSlotId('');
            }}
          >
            <option value="">Alege…</option>
            {lockers.data?.items.map((l) => (
              <option key={l.id} value={l.id}>
                {lockerOptionLabel(l)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Compartiment" error={errors.slotId} htmlFor="ro-slot">
          <Select
            id="ro-slot"
            value={slotId}
            disabled={!detail.data}
            onChange={(e) => setSlotId(e.target.value)}
          >
            <option value="">Alege…</option>
            {slots.map((x) => (
              <option key={x.id} value={x.id}>
                {x.label} ·{' '}
                {x.occupancy === 'free'
                  ? 'liber'
                  : x.occupancy === 'reserved'
                    ? 'rezervat'
                    : 'ocupat'}
                {x.doorOpen ? ' · ușă deschisă' : ''}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Motiv"
          hint={`${reason.trim().length}/${MIN_REASON} caractere minimum`}
          error={errors.reason}
          htmlFor="ro-reason"
        >
          <textarea
            id="ro-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="Descrie intervenția și motivul accesului fizic…"
            style={{
              width: '100%',
              border: '1px solid var(--line)',
              borderRadius: 9,
              padding: 10,
              font: 'inherit',
            }}
          />
        </Field>
        <Field label="Referință incident (opțional)" htmlFor="ro-ticket">
          <Input
            id="ro-ticket"
            value={ticket}
            onChange={(e) => setTicket(e.target.value)}
            maxLength={64}
          />
        </Field>
      </div>
    </Modal>
  );
}
